import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { blogApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      toast.error('You do not have permission to access this page');
      navigate('/blog');
    }
  }, [isAdmin, navigate]);

  // Determine if we're editing an existing post
  const isEditing = Boolean(id);

  // Fetch post data if editing
  useEffect(() => {
    if (isEditing && isAdmin) {
      setLoading(true);
      blogApi.getById(id)
        .then(response => {
          setPost(response.data);
          reset({
            title: response.data.title,
            slug: response.data.slug,
            content: response.data.content,
            excerpt: response.data.excerpt,
            imageUrl: response.data.imageUrl,
            tags: response.data.tags,
            published: response.data.published,
          });
        })
        .catch(error => {
          toast.error('Failed to load blog post');
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditing, reset, isAdmin]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditing) {
        await blogApi.update(id, data);
        toast.success('Blog post updated successfully');
      } else {
        await blogApi.create(data);
        toast.success('Blog post created successfully');
      }
      navigate('/blog');
    } catch (error) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} blog post`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
        </h1>

        {loading && !post && isEditing ? (
          <div className="text-center py-4">Loading post data...</div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="form-label">Title *</label>
                <input
                  id="title"
                  type="text"
                  className="form-input"
                  placeholder="Enter post title"
                  {...register('title', { required: 'Title is required' })}
                />
                {errors.title && <p className="form-error">{errors.title.message}</p>}
              </div>

              <div>
                <label htmlFor="slug" className="form-label">
                  Slug (URL-friendly identifier)
                </label>
                <input
                  id="slug"
                  type="text"
                  className="form-input"
                  placeholder="auto-generated-from-title (leave empty for auto-generation)"
                  {...register('slug')}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave empty to auto-generate from title
                </p>
              </div>

              <div>
                <label htmlFor="excerpt" className="form-label">Excerpt</label>
                <textarea
                  id="excerpt"
                  rows="3"
                  className="form-input"
                  placeholder="Brief summary of the post (shown in listings)"
                  {...register('excerpt')}
                />
              </div>

              <div>
                <label htmlFor="content" className="form-label">Content *</label>
                <textarea
                  id="content"
                  rows="15"
                  className="form-input"
                  placeholder="Write your blog post content here..."
                  {...register('content', { required: 'Content is required' })}
                />
                {errors.content && <p className="form-error">{errors.content.message}</p>}
              </div>

              <div>
                <label htmlFor="imageUrl" className="form-label">Image URL</label>
                <input
                  id="imageUrl"
                  type="url"
                  className="form-input"
                  placeholder="https://example.com/image.jpg"
                  {...register('imageUrl')}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Enter a URL for the featured image
                </p>
              </div>

              <div>
                <label htmlFor="tags" className="form-label">Tags</label>
                <input
                  id="tags"
                  type="text"
                  className="form-input"
                  placeholder="property, rent, management (comma-separated)"
                  {...register('tags')}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Separate tags with commas
                </p>
              </div>

              <div className="flex items-center">
                <input
                  id="published"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('published')}
                />
                <label htmlFor="published" className="ml-2 block text-sm text-gray-900">
                  Publish immediately
                </label>
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/blog')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditing ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BlogForm;
